import { Component, computed, effect, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { ImageTrackerDistributionSettings } from 'dashboard/interfaces/visuals/list-widget.interface';
import { VisualDashboardDistributor } from 'dashboard/interfaces/visuals/visual-dashboard-distributor.interface';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import { merge } from 'rxjs';

@Component({
    selector: 'app-visuals-dashboard-distribution-settings-picker',
    imports: [ReactiveFormsModule, TranslateModule],
    templateUrl: './visuals-dashboard-distribution-settings-picker.component.html',
    styleUrl: './visuals-dashboard-distribution-settings-picker.component.scss',
})
export class VisualsDashboardDistributionSettingsPickerComponent {
    widget = input.required<DashboardWidget>();
    distributorSettingsChanged = output<ImageTrackerDistributionSettings>();

    showDistributionDataForm = new FormGroup({
        showListingData: new FormControl(false),
        showPhotoData: new FormControl(false),
    });

    distributorSelectionForm = new FormGroup({});

    private formChanges = toSignal(merge(this.showDistributionDataForm.valueChanges, this.distributorSelectionForm.valueChanges));

    isSmall = computed(() => this.widget().selectedSize === VisualsDashboardWidgetSizingEnum.SMALL);

    availableDistributors = computed(() => {
        const htmlId = this.widget().htmlId;
        const distributorMap = this.visualsDashboardService.htmlIdToAvailableDistributorsMap();
        if (!htmlId) {
            return [];
        }
        return distributorMap[htmlId] ?? [];
    });

    distributorItems = computed(() => {
        const distributors = this.availableDistributors();
        return distributors
            .map((distributor) => {
                const control = this.distributorSelectionForm.get(String(distributor.distributorId)) as FormControl<boolean> | null;
                return control ? { ...distributor, control } : null;
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    });

    currentSettings = computed<ImageTrackerDistributionSettings>(() => {
        this.formChanges();

        const showData = this.showDistributionDataForm.value;
        const distributorValues = this.distributorSelectionForm.value as Record<string, boolean>;

        const selectedDistributorIds = Object.entries(distributorValues)
            .filter(([, isSelected]) => isSelected)
            .map(([distributorId]) => Number(distributorId));

        return {
            isListings: showData.showListingData ?? false,
            isPhotos: showData.showPhotoData ?? false,
            distributorIds: selectedDistributorIds,
        };
    });

    private isPatching = false;

    constructor(private visualsDashboardService: VisualsDashboardService) {
        effect(() => {
            const availableDistributors = this.availableDistributors();
            this.rebuildDistributorForm(availableDistributors);
        });

        effect(() => {
            const widget = this.widget();
            this.patchFormsFromWidget(widget);
        });

        effect(() => {
            const settings = this.currentSettings();
            if (this.isPatching) {
                return;
            }
            this.distributorSettingsChanged.emit(settings);
        });

        effect(() => {
            const isSmall = this.isSmall();

            if (isSmall) {
                this.showDistributionDataForm.disable({ emitEvent: false });
                this.distributorSelectionForm.disable({ emitEvent: false });
            } else {
                this.showDistributionDataForm.enable({ emitEvent: false });
                this.distributorSelectionForm.enable({ emitEvent: false });
            }
        });
    }

    private rebuildDistributorForm(distributors: VisualDashboardDistributor[]): void {
        const form = this.distributorSelectionForm;
        const currentKeys = Object.keys(form.controls);
        const newKeys = distributors.map((distributor) => String(distributor.distributorId));

        const keysToRemove = currentKeys.filter((key) => !newKeys.includes(key));
        for (const key of keysToRemove) {
            form.removeControl(key as never);
        }

        for (const key of newKeys) {
            if (!form.contains(key)) {
                form.addControl(key as never, new FormControl(false, { nonNullable: true }));
            }
        }
    }

    private patchFormsFromWidget(widget: DashboardWidget): void {
        const settings = widget.imageTrackerDistributionSettings;
        if (!settings) {
            return;
        }

        this.isPatching = true;

        this.showDistributionDataForm.patchValue({
            showListingData: settings.isListings ?? false,
            showPhotoData: settings.isPhotos ?? false,
        });

        const selectedIds = new Set(settings.distributorIds?.map(String) ?? []);
        for (const key of Object.keys(this.distributorSelectionForm.controls)) {
            this.distributorSelectionForm.get(key)?.setValue(selectedIds.has(key));
        }

        this.isPatching = false;
    }

    selectAllDistributors(): void {
        for (const key of Object.keys(this.distributorSelectionForm.controls)) {
            this.distributorSelectionForm.get(key)?.setValue(true);
        }
    }

    deselectAllDistributors(): void {
        for (const key of Object.keys(this.distributorSelectionForm.controls)) {
            this.distributorSelectionForm.get(key)?.setValue(false);
        }
    }
}
